// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, mergeWithCss, xClassNames, xScreen } from "@stoneage/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import React, { FC, useState } from "react";
import { css } from "styled-components";
import { fontConfig } from "@stoneage/XDL/constants";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";

export interface HeaderClickableItemProps {
    isOpen?: boolean;
    showIcon?: boolean;
    extendedStyles?: HeaderClickableStyles;
    customRightContent?: React.ReactNode;
    subTitle?: string;
    referenceId?: string;
}

export type HeaderClickableProps = HeaderClickableItemProps & ClickableProps;

type Props = HeaderClickableProps;

export interface HeaderClickableStyles {
    clickable?: ClickablePresentationProps;
    iconWrapper?: InjectableCss;
    icon?: IconProps;
    title?: TypographyPresentationProps;
    subTitle?: TypographyPresentationProps;
    openColor?: TypographyProps["color"];
}

export const headerClickableStyles: HeaderClickableStyles = {
    clickable: {
        css: css`
            display: flex;
            gap: 8px;
            padding: 8px 16px;
            height: 100%;
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;

            &:focus {
                outline: none;
            }

            ${xScreen("gt", "md")} {
                &:hover,
                &[data-state="open"] {
                    background: ${color("primary")};
                    .iconWrapper {
                        border-color: ${color("common.white")};
                        ${IconWrapper} {
                            color: ${color("common.white")};
                        }
                    }
                    ${TypographyStyle} {
                        color: ${color("common.white")};
                    }
                    // subtitle (e.g. "Or Register")
                    ${TypographyStyle} ${TypographyStyle} {
                        color: ${color("common.ui4")};
                    }
                }
            }

            ${xScreen("lte", "md")} {
                padding: 0;
            }
        `,
    },
    iconWrapper: {
        css: css`
            width: 34px;
            height: 34px;
            aspect-ratio: 1;
            padding: 8px;
            border: 1px solid ${color("primary")};
            border-radius: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    icon: {
        size: 16,
        color: "common.black",
    },
    title: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.medium,
        color: "secondary",
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    subTitle: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.regular,
        color: "text.main",
    },
};

const HeaderClickable: FC<Props> = ({
    isOpen,
    children,
    showIcon = true,
    extendedStyles,
    customRightContent,
    subTitle,
    referenceId,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));
    const className = xClassNames.append([isOpen && "opened"], otherProps.className);

    return (
        <Clickable {...styles.clickable} {...otherProps} className={className}>
            <StyledWrapper {...styles.iconWrapper} className="iconWrapper">
                <Icon {...styles.icon} />
            </StyledWrapper>
            <Typography {...styles.title}>
                {children}
                <Typography {...styles.subTitle}>{subTitle}</Typography>
            </Typography>
        </Clickable>
    );
};

export default HeaderClickable;
